import React, { useRef, useState } from 'react'
import { AMSearchRequest } from 'src/common-services'
import { nearbyPointFamily } from 'src/v2/state/map-explorer/search'
import { Colors, Shapes } from 'src/components/theme'
import styled from '@emotion/styled'
import { CircleResults, ResultsGrid } from 'src/views/map-explorer/sidebar/related-results'
import { Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import { GenericLoader } from 'src/v2/components/skeleton-loaders'
import { FlexRow } from 'src/components/flex-containers'
import { useAtomValueLoadable } from 'src/utils/jotai'
import { SidebarResultOverlay } from 'src/v2/components/sidebar-result-overlay'
import { SidebarResultListItem } from 'src/v2/components/sidebar-result-list'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { InfoCircle } from '@emotion-icons/fa-solid/InfoCircle'

const Wrapper = styled.div`
  margin-top: ${Shapes.spacing[1]}px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${Shapes.spacing[1]}px;
`

const ElbowWrapper = styled.div`
  position: relative;
  width: ${Shapes.spacing[4]}px;
  height: 36px;
  overflow: hidden;
`
const Elbow = styled.div`
  position: absolute;
  top: 0%;
  left: 50%;
  width: 50%;
  height: 50%;
  border-bottom: 1px solid ${Colors.neutral300};
  border-left: 1px solid ${Colors.neutral300};
`

const CircleResultsWrapper = styled.div`
  display: flex;
  gap: ${Shapes.spacing[2]}px;
  align-items: center;
  flex: 1;
  padding: ${Shapes.spacing[1]}px;
  border: 1px solid ${Colors.border};
  border-radius: ${Shapes.borderRadius}px;
  background-color: ${Colors.bgWhite};
  :hover {
    cursor: pointer;
    background-color: ${Colors.neutral50};
  }
`

const NearbyContent = (props: { request: AMSearchRequest }) => {
  const nearby = useAtomValue(nearbyPointFamily(props.request))
  const [showOverlay, setShowOverlay] = useState<boolean>(false)
  const parentRef = useRef<HTMLDivElement>(null)
  const { selectLocation } = useMapExplorerNavigation()

  const onClick = async (location: SidebarResultListItem) => {
    await selectLocation(location.id)
  }

  const onMouseEnterItem = () => {
    setShowOverlay(true)
  }
  const onMouseLeaveItem = () => {
    setShowOverlay(false)
  }
  const onCloseOverlay = () => {
    setShowOverlay(false)
  }

  if (!nearby) {
    return null
  }
  return (
    <>
      <CircleResultsWrapper ref={parentRef} onMouseEnter={onMouseEnterItem} onMouseLeave={onMouseLeaveItem}>
        <CircleResults results={[...nearby.shoppingCenters, ...nearby.locations].slice(0, 4)} />
        <FlexRow align="center" gap={Shapes.spacing[1]}>
          <Typography variant="text2m">
            ({nearby.locations.length + nearby.shoppingCenters.length}) locations near this address
          </Typography>
          <InfoCircle size={14} color={Colors.neutral400} />
        </FlexRow>
      </CircleResultsWrapper>
      <SidebarResultOverlay
        anchorEl={parentRef?.current}
        open={showOverlay}
        onClose={onCloseOverlay}
        maxWidth={672}
        contentPadding={Shapes.spacing[2]}>
        <ResultsGrid deduped={nearby} onClick={onClick} />
      </SidebarResultOverlay>
    </>
  )
}

export const NearbyPoint = (props: { request: AMSearchRequest; hideElbow?: boolean }) => {
  const nearbyLoadable = useAtomValueLoadable(nearbyPointFamily(props.request))
  if (nearbyLoadable.state === 'hasData' && !nearbyLoadable.data) {
    return null
  }
  return (
    <Wrapper>
      {!props.hideElbow && (
        <ElbowWrapper>
          <Elbow />
        </ElbowWrapper>
      )}
      <React.Suspense
        fallback={
          <CircleResultsWrapper>
            <FlexRow gap={Shapes.spacing[1]} align="center">
              <GenericLoader variant="circular" color="medium" height={24} width={24} />
              <GenericLoader variant="circular" color="medium" height={24} width={24} />
              <GenericLoader variant="circular" color="medium" height={24} width={24} />
              <GenericLoader variant="rounded" height={12} width={40} />
            </FlexRow>
          </CircleResultsWrapper>
        }>
        <NearbyContent request={props.request} />
      </React.Suspense>
    </Wrapper>
  )
}
